import { Controller } from "@hotwired/stimulus"

// Suggested thank-you page titles (#12908) — shared/_thank_you_title_suggestions.
//
// The chips live ABOVE a Title input they do not contain, so the input is found
// by id (inputId value). Tapping a chip writes the title into the input (capped
// at the input's own maxlength — the #12804 title cap) and fires `input` +
// `change` on it, so whatever the hosting surface already listens for runs
// exactly as it does for typing: realtime-order#thankYouFieldEdited (reveals
// "Save these as my profile defaults"), thank-you-step#refresh (live preview),
// unsaved-changes#trackChange (Settings). Typing stays free.
//
// Live state — recomputed on every keystroke and on `title-suggestions:sync`:
//   * the chip whose title matches the input is pressed (is-on / aria-pressed);
//   * the weak-title hint shows only while the input normalizes (trim, lower,
//     collapse whitespace) to one of weakTitles — the server-rendered list from
//     LeadTypeRegistry::WEAK_THANK_YOU_TITLES, never a copy kept here;
//   * a readonly / disabled input (the buy-for "agent's profile" source, the
//     legacy page's profile lock, the Lead Defaults profile-copy checkbox)
//     disables the chips, shows the locked line and puts lockedReason on every
//     chip as its tooltip — a disabled control explains why (A11Y-1).
//
// leadType value: the base class being bought. The chip for that type moves to
// the front; every other chip keeps the server's BASE order, which rides on each
// chip as data-rank (bought → generic → rest, WITHOUT the cart-type fronting).
// Re-fronting therefore always starts from the list a fresh load would render —
// never from a DOM that is already fronted, which would strand the previous
// type in slot 2 — and a re-connect cannot snapshot a reordered DOM. Chips
// without ranks keep their DOM order.
//
// realtime-order#syncTitleSuggestions writes the leadType value as the picker
// changes (render) and dispatches `title-suggestions:sync` after the writers
// that set the Title input WITHOUT an input event — resetSectionDefaults
// (profile defaults), applyOwnerValues ("My info") and setThankYouFieldsLocked
// (the buy-for lock). user-edit#syncTitleSuggestions does the same on Settings
// after the profile-copy checkbox writes, clears or disables the field.
//
// Wire: data-controller="title-suggestions"
//       data-title-suggestions-input-id-value="<id of the title input>"
//       data-title-suggestions-lead-type-value="VeteranLead|…|"
//       data-title-suggestions-weak-titles-value='["insurance agent", …]'
//       data-title-suggestions-locked-reason-value="<why the chips are inert while locked>"
//       data-action="title-suggestions:sync->title-suggestions#sync"
//       chips: data-title-suggestions-target="chip" data-title="…" data-lead-type="…"
//              data-rank="<base position>" data-action="title-suggestions#pick"
//       locked line: data-title-suggestions-target="lockedHint"
export default class extends Controller {
  static targets = ["chip", "chips", "hint", "lockedHint"]
  static values = { inputId: String, leadType: String, weakTitles: Array, lockedReason: String }

  static ON_CLASS = "is-on"
  static TOOLTIP_ATTR = "data-gl-tooltip"

  connect() {
    this.baseOrder = this.serverOrder()
    this.boundSync = () => this.sync()
    const input = this.input()
    if (input) {
      input.addEventListener("input", this.boundSync)
      input.addEventListener("change", this.boundSync)
    }
    this.reorder()
    this.sync()
  }

  disconnect() {
    const input = this.input()
    if (input && this.boundSync) {
      input.removeEventListener("input", this.boundSync)
      input.removeEventListener("change", this.boundSync)
    }
  }

  // Stimulus fires this once before connect() (baseOrder not captured yet)
  // and again whenever realtime-order rewrites the attribute.
  leadTypeValueChanged() {
    if (this.baseOrder) this.reorder()
  }

  pick(event) {
    const input = this.input()
    if (!input || input.readOnly || input.disabled) return
    let title = event.currentTarget.dataset.title || ""
    if (input.maxLength > 0) title = title.slice(0, input.maxLength)
    input.value = title
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
    this.sync()
  }

  sync() {
    const input = this.input()
    const current = this.normalize(input ? input.value : "")
    const locked = !!input && (input.readOnly || input.disabled)
    const reason = locked ? this.lockedReasonValue : ""
    this.chipTargets.forEach((chip) => {
      const on = current.length > 0 && this.normalize(chip.dataset.title) === current
      chip.classList.toggle(this.constructor.ON_CLASS, on)
      chip.setAttribute("aria-pressed", String(on))
      chip.disabled = locked
      if (reason) chip.setAttribute(this.constructor.TOOLTIP_ATTR, reason)
      else chip.removeAttribute(this.constructor.TOOLTIP_ATTR)
    })
    if (this.hasHintTarget) this.hintTarget.hidden = !this.isWeak(current)
    if (this.hasLockedHintTarget) this.lockedHintTarget.hidden = !locked
  }

  // The server's base order, read from data-rank when every chip carries one;
  // otherwise the DOM order as connected (fixtures and surfaces without ranks).
  serverOrder() {
    const chips = [...this.chipTargets]
    const ranked = chips.every((chip) => /^\d+$/.test(chip.dataset.rank ?? ""))
    return ranked ? chips.sort((a, b) => Number(a.dataset.rank) - Number(b.dataset.rank)) : chips
  }

  // The chip for the selected type first, everything else in the base order.
  reorder() {
    if (!this.hasChipsTarget || !this.baseOrder) return
    const type = this.leadTypeValue
    const match = type ? this.baseOrder.find((chip) => chip.dataset.leadType === type) : null
    const ordered = match ? [match, ...this.baseOrder.filter((chip) => chip !== match)] : this.baseOrder
    this.chipsTarget.append(...ordered)
  }

  isWeak(normalized) {
    if (normalized.length === 0) return false
    return this.weakTitlesValue.some((weak) => this.normalize(weak) === normalized)
  }

  // Mirrors RealtimeOrders::ThankYouTitleSuggestions.normalize (downcase + squish).
  normalize(value) {
    return String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ")
  }

  input() {
    return this.inputIdValue ? document.getElementById(this.inputIdValue) : null
  }
}
